import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { CANCEL_ERROR, CancelToken } from 'apisauce';
import {
  find as lodashFind,
  isEmpty as lodashIsEmpty,
  isEqual as lodashIsEqual,
  pick as lodashPick,
} from 'lodash';

import { ModalManager } from '../../../Components/Web/Modal/hooks/useModalManager';
import { MODALPROMPT } from '../../../Components/Web/Modal/ModalPrompt/config';
import constants from '../../../Config/constants';
import messages from '../../../Config/messages';
import validation from '../../../Config/validationRules';
import { isUserDetailsIncomplete } from '../../../Helper';
import CheckoutHelper from '../../../Helper/Checkout';
import MealPlanHelper from '../../../Helper/MealPlan';
import Sentry from '../../../Helper/Sentry';
import useAlert from '../../../Hooks/useAlert';
import useCancellableRequest from '../../../Hooks/useCancellableRequest';
import useClosedOrClosingPrompt from '../../../Hooks/useClosedOrClosingPrompt';
import useMealPlan from '../../../Hooks/useMealPlan';
import useToast from '../../../Hooks/useToast';
import routeList from '../../../Routes/list';
import { checkout } from '../../../RTK/defaultValues';
import { whenFilterSelector } from '../../../RTK/filter/selectors';
import { selectMealPlanCart } from '../../../RTK/mealPlan/selectors';
import { shopSelector } from '../../../RTK/shop/selectors';
import { updateInformation } from '../../../RTK/user';
import { userSelector } from '../../../RTK/user/selectors';
import { selectPage } from '../../../RTK/utility/selectors';
import Service from '../../../Service';
import checkoutApi from '../../../Service/api/checkout';
import storeApi from '../../../Service/api/store';
import userApi from '../../../Service/api/user';

import { paymentList } from '../PaymentOptions';
import { vehicleList } from '../VehicleOptions';

import useCheckoutHandler from './useCheckoutHandler';

const thirdPartySchema = validation.createSchema({
  firstName: validation.firstNameSchema,
  lastName: validation.lastNameSchema,
  phone: validation.contactSchema,
});

const vehicleInformationSchema = validation.createSchema({
  make: validation.fieldRequired('Vehicle Make'),
  color: validation.fieldRequired('Vehicle Color'),
  plateNumber: validation.fieldRequired('Vehicle Plate Number'),
});

const deliveryDefaultValue = {
  fee: undefined,
  priority_fee: 0,
  loading: false,
  error: '',
  mode: constants.DELIVERY_PAYMENT_OPTION.NON_CASH,
  partner: '',
  codFreeDel: false,
  vehicle: '',
};

const EMPTY_ARRAY = []; // outside lifecycle to prevent new reference on rerender
function useCheckout() {
  const alert = useAlert();
  const dispatch = useDispatch();
  const { isMealPlan, mealPlanCartCounter, mealPlanDayCount, mealPlanType } =
    useMealPlan();
  const {
    checkoutData,
    checkoutInfo,
    checkoutItems,
    formDeliveryAddress,
    formDeliverySchedule,
    formDeliverySpeed,
    formPaymentMethod,
    formPaymentOptions,
    formPromoCode,
    formThirdPartyPickDetails,
    formUserDetails,
    formVehicleInformation,
    formVehicleOptions,
    isCurbside,
    isDelivery,
    isPickup,
    orderType,
    onUpdateCheckoutDetails,
  } = useCheckoutHandler();
  const cancelSource = useRef(CancelToken.source());
  const scrollRef = useRef();
  const delEstimateRef = useRef({ showScheduleListModal: () => {} });
  const navigation = useNavigation();
  const toast = useToast();
  const page = useSelector(selectPage);
  const { createRequest } = useCancellableRequest();
  const { showClosedOrClosingPrompt } = useClosedOrClosingPrompt();

  const mealPlanAllScheduleCart = useSelector(selectMealPlanCart);
  const savedPayment = useSelector(
    (state) => state.utility.settings.savedPayment,
  );
  const storeMealPlanDeliveryDiscount = useSelector(
    (state) => shopSelector(state)?.free_delivery_amount,
  );
  const storeMealPlanDiscount = useSelector(
    (state) => shopSelector(state)?.meal_plan_discount,
  );
  const user = useSelector(userSelector);
  const whenFilter = useSelector(whenFilterSelector);

  const [deliveryQuotation, setDeliveryQuotation] =
    useState(deliveryDefaultValue);
  const [isPlacingOrder, setPlacingOrder] = useState(false);

  const hasBulk = !lodashIsEmpty(lodashFind(checkoutItems, { is_bulk: true }));

  // below 3 function needs to be at the top of useQuery and effect in order for this to work
  const _getDeliveryQuotation = useCallback(async () => {
    if (
      !page.isCheckout ||
      (!isDelivery && !isMealPlan) ||
      !formVehicleOptions?.value
    ) {
      // if gets called if user is not in checkout page OR selected order type is not delivery OR no vehicle option is selected (special condition because, sometimes on unmount selected vehicle option needs to be removed)
      cancelSource.current?.cancel?.(); // cancel previous pending request
      setDeliveryQuotation(deliveryDefaultValue); // set the delivery quotation state to default
      return; // stop the code from going
    }
    // reset delivery quotation state and make loading as true
    setDeliveryQuotation({ ...deliveryDefaultValue, loading: true });
    // payload construction
    const payload = {
      store_id: checkoutInfo.store_id,
      delivery_payment_method: formPaymentOptions?.value,
      delivery_vehicle: formVehicleOptions?.value,
      order_date: formDeliverySchedule?.date || undefined, // in case empty string, if undefined it will automatically remove it self from payload
      order_time: formDeliverySchedule?.time || undefined, // in case empty string, if undefined it will automatically remove it self from payload
      meal_plan_type: mealPlanType || undefined, // in case empty string, if undefined it will automatically remove it self from payload
      address: {
        lat: formDeliveryAddress?.geo_json_point.coordinates[1],
        lng: formDeliveryAddress?.geo_json_point.coordinates[0],
        address_string: formDeliveryAddress?.formatted_address,
      },
      pricing_tier_code: formDeliverySpeed,
    };
    // api request
    cancelSource.current?.cancel?.(); // cancel previous pending request
    cancelSource.current = CancelToken.source();
    const { ok, data, problem } = await checkoutApi.getDeliveryFeeQuotation(
      payload,
      cancelSource.current.token,
    );
    // stop the code from going if request is cancelled
    if (problem === CANCEL_ERROR) {
      return;
    }
    const stateToUpdate = {
      ...deliveryDefaultValue,
      vehicle: payload.delivery_vehicle,
      mode: payload.delivery_payment_method,
    };
    // handling api result
    if (ok) {
      // update only the delivery fee
      if (data?.selected?.is_success) {
        const { selected } = data;
        const mealPlanDeliveryFeePromo = isMealPlan
          ? storeMealPlanDeliveryDiscount
          : 0;
        const totalFreeDelivery = isMealPlan
          ? mealPlanDeliveryFeePromo * mealPlanDayCount
          : 0;
        const actualTotalDeliveryFee =
          totalFreeDelivery >= selected.delivery_fee
            ? 0
            : selected.delivery_fee - totalFreeDelivery;
        stateToUpdate.fee = actualTotalDeliveryFee;
        stateToUpdate.priority_fee = selected.priority_fee;
        stateToUpdate.partner = selected.partner;
      } else {
        stateToUpdate.error =
          data?.selected?.message?.errors?.[0]?.message ||
          Service.handleErrorMessage(
            data?.selected?.message || messages.COMMON_ERROR_MESSAGE,
          );
        if (constants.isWeb) {
          ModalManager.show(MODALPROMPT.prompt, {
            payload: {
              title: 'Error',
              message:
                data?.selected?.message?.errors?.[0]?.message ||
                Service.handleErrorMessage(
                  data?.selected?.message || messages.COMMON_ERROR_MESSAGE,
                ),
            },
          });
        }
      }
    } else {
      const errMsg = data?.message?.[0] || 'Quotation Error';
      Sentry.reportError('Error getting delivery quotation', data);
      stateToUpdate.error = Service.handleErrorMessage(errMsg);
      if (
        errMsg === 'address - store maximum delivery distance limit exceeded'
      ) {
        alert(
          'Oopss! Out of delivery zone',
          'The address you have selected is outside the delivery range. Please select a different address or try with our other stores.',
        );
      } else if (errMsg?.includes?.('ERR_UNKNOWN')) {
        // error message for third party delivery quotation error
        alert(
          'Delivery Quotation Unavailable',
          'We regret to inform you that there is an issue retrieving the third-party delivery quotation at the moment. Please try again later or contact customer support for assistance.',
        );
      } else {
        // Generic error message for failed delivery quotation
        alert(
          'Delivery Quotation Unavailable',
          'We regret to inform you that there is an issue with retrieving the delivery quotation. Please retry or contact customer support for assistance.',
        );
      }
    }
    setDeliveryQuotation(stateToUpdate);
  }, [
    checkoutInfo.store_id,
    formDeliveryAddress,
    formDeliverySchedule,
    formDeliverySpeed,
    formPaymentOptions,
    formVehicleOptions,
    isDelivery,
    isMealPlan,
    mealPlanDayCount,
    mealPlanType,
    page.isCheckout,
    storeMealPlanDeliveryDiscount,
    alert,
  ]);
  const _setPreOrderDateOrAsapSchedule = (dateValue, orderDates) => {
    const isAlreadyAsap =
      formDeliverySchedule?.value === CheckoutHelper.ASAP.value;
    const isCanAsap = CheckoutHelper.isCanAsap(orderDates);
    const { ok, data } = CheckoutHelper.getDateFromStoreHours(
      dateValue,
      orderDates,
    );
    if (ok) {
      // if date filter is found on store hours, it means it's still possible to proceed on date filter schedule
      onUpdateCheckoutDetails(checkout.keys.DELIVERY_SCHEDULE, data); // set schedule as date filter
    } else if (isCanAsap && !isAlreadyAsap) {
      // if can order asap AND not yet selected asap
      alert(
        'Order date updated',
        'Your previous pre-order date has been removed. Your order is now set for ASAP.',
      );
      // set schedule as asap
      onUpdateCheckoutDetails(
        checkout.keys.DELIVERY_SCHEDULE,
        CheckoutHelper.ASAP,
      );
    } else if (!isCanAsap && !lodashIsEmpty(formDeliverySchedule)) {
      // if cannot order asap AND still has pre-order date
      alert(
        'Order date updated',
        'We have removed your pre-order date as it was outdated. Please select new schedule.',
      );
      onUpdateCheckoutDetails(checkout.keys.DELIVERY_SCHEDULE); // remove schedule
    }
  };
  const _setDefaultScheduleValue = async (result) => {
    const isCanAsap = CheckoutHelper.isCanAsap(result.data);
    if (
      !lodashIsEmpty(formDeliverySchedule) &&
      formDeliverySchedule?.value !== CheckoutHelper.ASAP.value
    ) {
      // has existing selected schedule AND it's not ASAP
      _setPreOrderDateOrAsapSchedule(formDeliverySchedule.value, result.data);
    } else if (!lodashIsEmpty(whenFilter)) {
      // User set a date filter on homepage
      _setPreOrderDateOrAsapSchedule(whenFilter.value, result.data);
    } else if (isCanAsap) {
      // user doesn't have date filter or no selected schedule yet BUT can asap
      onUpdateCheckoutDetails(
        checkout.keys.DELIVERY_SCHEDULE,
        CheckoutHelper.ASAP,
      );
    }
    _getDeliveryQuotation(); // after setting the schedule, get delivery quotation
  };

  const enableFetchOrderDates = Boolean(checkoutInfo.store_id) && !isMealPlan;
  const {
    data: storeHoursResult,
    isFetching: storeHoursLoading,
    refetch: refetchStoreHours,
  } = useQuery({
    enabled: enableFetchOrderDates,
    queryFn: () => storeApi.getStoreOrderDates(checkoutInfo.store_id),
    queryKey: [checkoutInfo.store_id, 'store-hours'],
    refetchOnWindowFocus: false,
    onSettled: _setDefaultScheduleValue,
  });

  // effect for setting default value and cancelling pending request when checkout page closed
  useEffect(() => {
    _setDefaultValues(); // set checkout default values (common form for normal and meal plan checkout)
    return () => {
      cancelSource.current?.cancel?.(); // when checkout page is closed, clear pending quotation request
    };
  }, []);

  // effect to handle refresh on meal plan checkout (when they refresh while on meal plan checkout, it will clear the meal plan state)
  useEffect(() => {
    if (constants.isWeb) {
      const hasVisited =
        sessionStorage.getItem(
          constants.MEAL_PLAN_CHECKOUT_VISITED_SESSION_KEY,
        ) === 'true';
      if (hasVisited) {
        sessionStorage.removeItem(
          constants.MEAL_PLAN_CHECKOUT_VISITED_SESSION_KEY,
        );
        setTimeout(navigation.goBack, 0); // wait reflect the clean up of meal plan before going back
      } else if (isMealPlan) {
        sessionStorage.setItem(
          constants.MEAL_PLAN_CHECKOUT_VISITED_SESSION_KEY,
          'true',
        ); // set flag
        window.onbeforeunload = () => ''; // show warning when refresh
      }
    }
  }, [checkoutInfo.store_id, isMealPlan, navigation]);

  // effect for calling delivery quotation
  useEffect(() => {
    if (isDelivery || isMealPlan) {
      _getDeliveryQuotation();
    }
  }, [
    // no need to add dependency for schedule and order type because
    // on load upon setting default form value quotation should be called
    isDelivery,
    isMealPlan,
    _getDeliveryQuotation,
  ]);

  const _setDefaultValues = async () => {
    // ===== set default payment method =====
    const defaultPaymentMethod = savedPayment.filter((p) => p.default);
    if (
      lodashIsEmpty(formPaymentMethod) &&
      !lodashIsEmpty(defaultPaymentMethod)
    ) {
      onUpdateCheckoutDetails(
        checkout.keys.PAYMENT_METHOD,
        defaultPaymentMethod[0],
      );
    }
    // ===== set default user details =====
    const isUserDetailsUpdated = !lodashIsEqual(
      lodashPick(user, ['first_name', 'last_name', 'phone']),
      {
        first_name: formUserDetails?.firstName,
        last_name: formUserDetails?.lastName,
        phone: formUserDetails?.phone,
      },
    );
    if (
      lodashIsEmpty(formUserDetails) ||
      isUserDetailsIncomplete(formUserDetails, true) ||
      isUserDetailsUpdated
    ) {
      onUpdateCheckoutDetails(checkout.keys.USER_DETAILS, {
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        phone: user.phone?.replace('+63', '') || '',
        email: user.email || '',
      });
    }
    // ===== set default selected address using the first user addressed (its an active address/default) =====
    const isPreviousAddressDeleted = !user.addresses
      .map((e) => e.place_id)
      .includes(formDeliveryAddress?.place_id);
    if (
      lodashIsEmpty(formDeliveryAddress) || // if no checkout delivery address yet OR
      isPreviousAddressDeleted // previous selected address is deleted on user addresses
    ) {
      onUpdateCheckoutDetails(
        checkout.keys.DELIVERY_ADDRESS,
        user.addresses[0],
      );
    }
    // ===== set default delivery speed =====
    const isNotPrioOrEmpty = checkoutInfo.use_prio_speed_as_default
      ? formDeliverySpeed !== '2' // undefined or "1" will return 'true' if the value is not "2"
      : lodashIsEmpty(formDeliverySpeed); // "1" will return false
    if (isNotPrioOrEmpty) {
      onUpdateCheckoutDetails(
        checkout.keys.DELIVERY_SPEED,
        checkoutInfo.use_prio_speed_as_default ? '2' : '1',
      );
    }
    // ===== set default delivery payment options =====
    if (lodashIsEmpty(formPaymentOptions)) {
      // if no payment option is selected yet, auto select non_cash if has, but if only 1, auto select it
      const delPymntMthds = isMealPlan
        ? ['non_cash']
        : checkoutInfo.delivery_payment_methods;
      const availableOption = lodashFind(paymentList, {
        value:
          delPymntMthds.length === 1
            ? delPymntMthds[0]
            : constants.DELIVERY_PAYMENT_OPTION.NON_CASH,
      });
      onUpdateCheckoutDetails(checkout.keys.PAYMENT_OPTIONS, availableOption);
    }
    // ===== set default vehicle option =====
    const noVehicleSelected = lodashIsEmpty(formVehicleOptions);
    const isCarSelected = lodashIsEqual(vehicleList[0], formVehicleOptions);
    if (hasBulk && !isCarSelected) {
      // if has bulky item and car is not yet selected, select car
      onUpdateCheckoutDetails(checkout.keys.VEHICLE_OPTIONS, vehicleList[0]);
    } else if (!hasBulk && (noVehicleSelected || isCarSelected)) {
      // if has no bulky item and no vehicle is selected yet or car is selected, select motorcycle
      onUpdateCheckoutDetails(checkout.keys.VEHICLE_OPTIONS, vehicleList[1]);
    }
    if (!isMealPlan) {
      // set default value for not meal plan checkout
      if (
        lodashIsEmpty(orderType) ||
        orderType?.value === constants.ORDER_TYPES.MEAL_PLAN
      ) {
        // ===== set the order type to the first available order type =====
        const firstActiveKey = lodashFind(
          Object.keys(checkoutInfo.order_types),
          (key) => checkoutInfo.order_types[key],
        ); // get the first active key e.g: is_delivery_active: true
        const firstOrderType = lodashFind(constants.ORDER_TYPE_DATA, {
          apiDataKey: firstActiveKey,
        }); // find that key on order type data
        onUpdateCheckoutDetails(checkout.keys.ORDER_TYPE, firstOrderType); // update order type
      }
    }
  };

  const _validateForm = async (requiredData) => {
    let success = true;
    for (let data of requiredData) {
      const cdata = checkoutData[checkout.keys[data.key]]; // cdata = checkout data
      const isUserDetails = data.key === 'USER_DETAILS';
      const isCard = data.key === 'PAYMENT_METHOD' && cdata?.method === 'card';
      const inCompleteUserDetails = isUserDetails
        ? isUserDetailsIncomplete(cdata, true)
        : false;
      const isEmpty = lodashIsEmpty(cdata);
      const hasValidationSchema = !lodashIsEmpty(data.validationSchema);
      let title = '';
      let message = '';

      if (isEmpty || inCompleteUserDetails) {
        title = 'Information Required';
        message = data.message || `${data.label} is required.`;
        if (inCompleteUserDetails) {
          scrollRef.current?.scrollTo({ y: 0, animated: true });
        }
      } else if (hasValidationSchema) {
        // has validation schema from Yup, run it
        await new Promise((resolve) => {
          data.validationSchema
            .validate(data.getValidationPayload(cdata))
            .then(() => resolve())
            .catch((err) => {
              title = 'Information Required';
              message = err.message;
              resolve();
            });
        });
      } else if (isCard) {
        // if card, and card info is not completed
        const result = CheckoutHelper.validateCard(
          cdata?.cardNumber,
          cdata?.cardMonth,
          cdata?.cardYear,
          cdata?.cardCvv,
        );
        if (!result.isValid) {
          title = result.title;
          message = result.message;
        }
      }
      if (!!title && !!message) {
        success = false;
        toast.hide();
        toast.show(`${title}\n${message}`, false);
        break; // stop loop
      }
    }
    return success;
  };

  const _isCanPlaceOrder = async (storeInfo) => {
    const isScheduleAsap =
      formDeliverySchedule?.value === CheckoutHelper.ASAP.value;
    // check if the store is already closed or closing
    const { isClosed, isClosing } = CheckoutHelper.isCanCheckout(
      storeInfo.store_hours,
      storeInfo.store_off_dates,
      storeInfo.prep_time,
      isScheduleAsap ? undefined : formDeliverySchedule?.value,
    );
    // basic required info
    const requiredInfo = [
      { key: 'USER_DETAILS', label: 'User details' },
      { key: 'DELIVERY_ADDRESS', label: 'Address' },
      { key: 'PAYMENT_METHOD', message: messages.PAYMENT_METHOD.required },
    ];
    // conditional required info
    if (!isMealPlan) {
      // only required order type and delivery schedule for non meal plan
      requiredInfo.push(
        // order type is automatically delivery for meal plan
        {
          key: 'ORDER_TYPE',
          message: 'Please choose a order type method.',
        },
        // no schedule needed for meal plan
        { key: 'DELIVERY_SCHEDULE', label: 'Schedule' },
      );
    }
    if (isDelivery || isMealPlan) {
      requiredInfo.push(
        {
          key: 'PAYMENT_OPTIONS',
          message: 'Please choose a delivery payment option.',
        },
        { key: 'VEHICLE_OPTIONS', label: 'Delivery Vehicle Option' },
      );
    } else if (isPickup && formThirdPartyPickDetails?.isChecked) {
      requiredInfo.push({
        key: 'THIRD_PARTY_PICKUP',
        message: 'Missing pick-up information',
        validationSchema: thirdPartySchema,
        getValidationPayload: (cdata) => ({
          phone: cdata?.contactNumber,
          lastName: cdata?.lastName,
          firstName: cdata?.firstName,
        }),
      });
    } else if (isCurbside) {
      requiredInfo.push({
        key: 'VEHICLE_INFORMATION',
        label: 'Vehicle information',
        validationSchema: vehicleInformationSchema,
        getValidationPayload: (cdata) => ({
          make: cdata?.make,
          color: cdata?.color,
          plateNumber: cdata?.plateNumber,
        }),
      });
    }
    const isOk = await _validateForm(requiredInfo);
    return {
      ok: isOk && (!isClosed || isClosing),
      isClosed,
      isClosing,
    };
  };

  const _onRequestCheckout = async () => {
    if (isUserDetailsIncomplete(user)) {
      // user doesn't setup his user details yet, so on the normal checkout
      // we update user details on the api using it before making api call for placing the order
      const updateUserPayload = {
        first_name: formUserDetails.firstName?.trim?.(),
        last_name: formUserDetails.lastName?.trim?.(),
        phone_number: formUserDetails.phone?.trim?.(),
      };
      await userApi.update(updateUserPayload);
      dispatch(updateInformation(formUserDetails));
    }
    const payload = {
      address_id: formDeliveryAddress.id,
      order_type: isMealPlan ? constants.ORDER_TYPES.DELIVERY : orderType.value,
      payment_method: formPaymentMethod?.method,
      promo_code: formPromoCode?.code?.toUpperCase(),
      store_id: checkoutInfo.store_id,
      pricing_tier_code: formDeliverySpeed,
    };
    if (isMealPlan) {
      payload.items = MealPlanHelper.getBasePayload({
        storeId: payload.store_id,
        data: mealPlanAllScheduleCart,
      }).items;
    }
    if (isDelivery || isMealPlan) {
      payload.delivery_option = {
        delivery_payment_method: formPaymentOptions?.value,
        delivery_vehicle: formVehicleOptions?.value,
      };
    }
    if (
      payload.order_type === constants.ORDER_TYPES.PICKUP &&
      formThirdPartyPickDetails?.isChecked
    ) {
      payload.order_type = constants.ORDER_TYPES.THIRD_PARTY_PICKUP;
      payload.third_party_pickup_option = {
        first_name: formThirdPartyPickDetails.firstName,
        last_name: formThirdPartyPickDetails.lastName,
        mobile_number: `0${formThirdPartyPickDetails.contactNumber}`,
      };
    }
    if (payload.order_type === constants.ORDER_TYPES.CURBSIDE) {
      payload.curbside_pickup_option = {
        vehicle_make: formVehicleInformation.make,
        vehicle_color: formVehicleInformation.color,
        vehicle_plate_number: formVehicleInformation.plateNumber,
      };
    }
    if (formDeliverySchedule?.date && formDeliverySchedule?.time) {
      payload.order_date = formDeliverySchedule.date;
      payload.order_time = formDeliverySchedule.time;
    }
    return createRequest(
      isMealPlan ? checkoutApi.placeOrderMealPlan : checkoutApi.placeOrder,
      payload,
    );
  };

  const _onPlaceOrder = async () => {
    const { isClosed, isClosing, ok } = await _isCanPlaceOrder(checkoutInfo);
    if (isClosed || isClosing) {
      const isAcceptingAdvanceOrder =
        checkoutInfo.days_accepting_in_advance !== 0;
      showClosedOrClosingPrompt({
        isAcceptingAdvanceOrder,
        isClosed,
        onBack: navigation.goBack,
        onReschedule: delEstimateRef.current?.showScheduleListModal,
      });
    } else if (ok) {
      _togglePlaceOrder();
    }
  };

  /**
   * Use for toggling the place order modal it has a delay due to modal closing behavior on swift side
   * @param {boolean} isClose - if true need to have delay before updating the state
   * @returns {void}
   */
  const _togglePlaceOrder = (isClose) => {
    return new Promise((resolve) => {
      setPlacingOrder((prev) => !prev);
      setTimeout(resolve, isClose ? 500 : 0);
    });
  };

  const _goBackToStore = () => {
    sessionStorage.removeItem(constants.MEAL_PLAN_CHECKOUT_VISITED_SESSION_KEY);
    // wait to remove session before navigating
    setTimeout(() => {
      navigation.navigate(routeList.HOME, {
        screen: 'MainMenu',
        params: {
          screen: routeList.STORE,
          params: { id: checkoutInfo.store_id },
        },
      });
    }, 0);
  };

  return {
    ref: {
      deliveryEstimate: delEstimateRef,
      scroll: scrollRef,
    },
    state: {
      isPlacingOrder,
    },
    deliveryQuotation,
    hasBulk,
    isStoreHoursLoading: storeHoursLoading,
    storeHours: storeHoursResult?.ok ? storeHoursResult.data : EMPTY_ARRAY,
    storeHoursError:
      storeHoursResult?.ok === false ? storeHoursResult.data : '',
    storeMealPlanDiscount,
    goBackToStore: _goBackToStore,
    onPlaceOrder: _onPlaceOrder,
    onRequestCheckout: _onRequestCheckout,
    refetchStoreHours,
    togglePlaceOrder: _togglePlaceOrder,
  };
}

export default useCheckout;
